import { useState } from "react";
import {
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  Box,
} from "@mui/material";
import {
  Plus,
  Search,
  Settings,
  RotateCw,
  FilterX,
  X,
  Eye,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "designAZ", label: "Design Name (A-Z)" },
  { value: "designZA", label: "Design Name (Z-A)" },
  { value: "priceHigh", label: "Price (High to Low)" },
  { value: "priceLow", label: "Price (Low to High)" },
];

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
};

const DeleteConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  recipe,
  isLoading,
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Delete Design Recipe
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete the design recipe "
              {recipe?.designName}"?
            </p>

            {/* Impact Information */}
            <div className="p-3 rounded-lg text-sm mb-4 bg-red-50 text-red-600">
              <p className="font-medium mb-1">Impact of Deletion:</p>
              <ul className="text-left space-y-1">
                <li>
                  • Design recipe will be permanently removed from the system
                </li>
                <li>• All associated production data will be deleted</li>
                <li>• This action cannot be undone</li>
                <li className="font-medium mt-2">
                  ⚠️ This design recipe has associated data that will be
                  affected
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className="px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-red-600 hover:bg-red-700">
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Recipe"
                )}
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

// Sample data for demonstration
const recipes = [
  {
    id: "DR-001",
    designCode: "DSN-001",
    designName: "Classic Cotton Shirt",
    photo:
      "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    price: 1299,
    createdDate: "15",
    createdMonth: "MAY",
    lastModified: "2024-05-15",
    status: "Active",
  },
  {
    id: "DR-002",
    designCode: "DSN-002",
    designName: "Summer Floral Dress",
    photo:
      "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    price: 2499,
    createdDate: "14",
    createdMonth: "MAY",
    lastModified: "2024-05-14",
    status: "Draft",
  },
  {
    id: "DR-003",
    designCode: "DSN-003",
    designName: "Denim Jacket",
    photo:
      "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    price: 1899,
    createdDate: "13",
    createdMonth: "MAY",
    lastModified: "2024-05-13",
    status: "Active",
  },
];

const DesignRecipe = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Define columns
  const defaultColumns = [
    { id: "sno", label: "S. No.", required: true },
    { id: "photo", label: "Photo", required: true },
    { id: "designCode", label: "Design ID", required: true },
    { id: "designName", label: "Design Name/Number", required: true },
    { id: "price", label: "Price", required: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState({
    sno: true,
    photo: true,
    designCode: true,
    designName: true,
    price: true,
  });

  // Debug log to check visibleColumns
  console.log("visibleColumns:", visibleColumns);
  console.log("defaultColumns:", defaultColumns);

  const [filters, setFilters] = useState({
    dateRange: "",
    sortBy: "",
  });

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate refresh
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      dateRange: "",
      sortBy: "",
    });
  };

  const handleView = recipeId => {
    navigate(`/production/design-recipe/view?id=${recipeId}`);
  };

  const handleDelete = recipe => {
    setRecipeToDelete(recipe);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!recipeToDelete) return;

    setIsDeleting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Handle delete logic here
      console.log("Recipe deleted:", recipeToDelete.id);

      setDeleteModalOpen(false);
      setRecipeToDelete(null);
    } catch (error) {
      console.error("Failed to delete recipe:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Design Recipes
          </h1>
        </div>

        {/* Search Bar */}
        <div className="flex items-center gap-4">
          <div className="w-[450px]">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by Design Code, Design Name..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={20}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100"
              title="Column Settings">
              <Settings size={20} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {/* Date Range Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={filters.dateRange}
                label="Date Range"
                onChange={e =>
                  setFilters({ ...filters, dateRange: e.target.value })
                }>
                <MenuItem value="">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={e =>
                  setFilters({ ...filters, sortBy: e.target.value })
                }>
                {sortOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Clear Filters Button */}
          {(searchQuery ||
            Object.values(filters).some(value => value !== "")) && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                       hover:bg-gray-100 rounded-lg transition-colors">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="px-6">
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid #E5E7EB",
            borderRadius: "0.5rem",
          }}>
          <TableContainer
            sx={{ maxHeight: "calc(100vh - 300px)" }}
            className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    S. No.
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    Photo
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    Design ID
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    Design Name/Number
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    Price
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    Recipe
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipes.map((recipe, index) => (
                  <TableRow
                    hover
                    key={recipe.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                        fontWeight: 500,
                      }}
                      className="font-outfit">
                      {index + 1}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      <img
                        src={recipe.photo}
                        alt={recipe.designName}
                        className="w-12 h-12 object-cover rounded-lg mx-auto"
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                        fontWeight: 500,
                      }}
                      className="font-outfit">
                      {recipe.designCode}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                      }}
                      className="font-outfit">
                      {recipe.designName}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#4B5563",
                        fontWeight: 500,
                      }}
                      className="font-outfit">
                      ₹{recipe.price.toLocaleString()}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "12px 16px",
                        fontSize: "14px",
                      }}
                      className="font-outfit">
                      <button
                        onClick={() => handleView(recipe.id)}
                        className="px-3 py-1.5 text-[#5652B0] bg-[#5652B0]/10 rounded-lg text-sm hover:bg-[#5652B0]/20 transition-all duration-200"
                        style={{ fontWeight: 550 }}>
                        Recipe
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                {recipes.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#6B7280",
                      }}
                      className="font-outfit">
                      No design recipes found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        recipe={recipeToDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default DesignRecipe;
